var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from "reselect";
import { AccountLOB, AccountStatus, AccountType, } from "../types";
import { getCustomizations } from "../../customization/selectors";
import { cdProductCodes, inactiveAccountStatus } from "./constants";
import { getAllAccounts } from "./selectors";
import { getByType, filterExpiredAccount, checkOwnershipType, filterByRetirementIndicator, formatDepositAccountsForDropdown, } from "./utils";
/**
 * Returns all deposit accounts or an empty array.
 */
var getAllDepositAccounts = createSelector(getAllAccounts, function (accounts) { var _a; return (_a = accounts === null || accounts === void 0 ? void 0 : accounts.filter(function (account) { return account.lob === AccountLOB.DEPOSIT; })) !== null && _a !== void 0 ? _a : []; });
var getOpenInternalDepositAccounts = createSelector(getAllDepositAccounts, function (accounts) {
    return accounts.filter(function (account) {
        var _a;
        var status = account.status;
        return (status !== AccountStatus.CLOSED &&
            !!((_a = account.indicator) === null || _a === void 0 ? void 0 : _a.external) === false);
    });
});
var getDebitCardEligibleAccounts = createSelector(getOpenInternalDepositAccounts, getByType(AccountType.DDA, AccountType.MMA, AccountType.CMG));
/**
 * Returns all active accounts and any accounts closed within 60 days
 */
var getHasActiveDepositAccounts = createSelector(getAllDepositAccounts, function (accounts) { return accounts.some(filterExpiredAccount); });
var getHasTrusteeOnlyDepositAccounts = createSelector(getAllDepositAccounts, function (accounts) {
    return accounts.length > 0 && accounts.every(checkOwnershipType);
});
/**
 * Returns true/false whether any deposit accounts have the `lastUpdated` property defined
 */
var getHasStaleAccounts = createSelector(getAllDepositAccounts, function (accounts) {
    return accounts.some(function (account) { return !!account.lastUpdated; });
});
var getNonRetirementAccounts = createSelector(getAllDepositAccounts, function (accounts) {
    return accounts.filter(filterByRetirementIndicator(false));
});
var getRetirementAccounts = createSelector(getAllDepositAccounts, function (accounts) {
    return accounts.filter(filterByRetirementIndicator(true));
});
var getNonRetirementAccountsByType = function (accType) {
    return createSelector(getNonRetirementAccounts, getByType(accType));
};
var getDDANonRetirement = getNonRetirementAccountsByType(AccountType.DDA);
var getMMANonRetirement = getNonRetirementAccountsByType(AccountType.MMA);
var getSDANonRetirement = getNonRetirementAccountsByType(AccountType.SDA);
var getCMGNonRetirement = getNonRetirementAccountsByType(AccountType.CMG);
var getCDANonRetirement = getNonRetirementAccountsByType(AccountType.CDA);
var getDepositAccountsForDropdown = createSelector(getDDANonRetirement, getMMANonRetirement, getSDANonRetirement, getCMGNonRetirement, getCDANonRetirement, getRetirementAccounts, function (_, customizations) { return getCustomizations(customizations); }, function () {
    var allSelectors = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        allSelectors[_i] = arguments[_i];
    }
    return formatDepositAccountsForDropdown.apply(void 0, __spreadArray([], __read(allSelectors), false));
});
export var getManageableCDs = createSelector(getAllAccounts, function (accounts) {
    var toUpper = function (status) {
        return status.toUpperCase();
    };
    return accounts === null || accounts === void 0 ? void 0 : accounts.filter(function (account) {
        return (account.productId &&
            cdProductCodes.includes(account.productId) &&
            !inactiveAccountStatus
                .map(toUpper)
                .includes(account.status.toUpperCase()) &&
            !account.closeDate);
    });
});
export default {
    getAll: getAllDepositAccounts,
    getOpenInternal: getOpenInternalDepositAccounts,
    getDebitCardEligible: getDebitCardEligibleAccounts,
    getIsActive: getHasActiveDepositAccounts,
    getHasTrusteeOnly: getHasTrusteeOnlyDepositAccounts,
    getHasStaleAccounts: getHasStaleAccounts,
    getAllForDropdown: getDepositAccountsForDropdown,
    getManageableCDs: getManageableCDs,
    retirement: {
        getAll: getRetirementAccounts,
    },
    nonRetirement: {
        getAll: getNonRetirementAccounts,
        getByType: getNonRetirementAccountsByType,
        getDDA: getDDANonRetirement,
        getMMA: getMMANonRetirement,
        getSDA: getSDANonRetirement,
        getCMG: getCMGNonRetirement,
        getCDA: getCDANonRetirement,
    },
};
//# sourceMappingURL=deposit.js.map